import React from 'react';
import toast from 'react-hot-toast';
import { BASE_URL } from '../../utils/api';
import { QrCode, Upload, Trash2, Loader2, Building2, CreditCard } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = React.useState({
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPass: '',
    adminNotificationEmail: '',
    paymentQrCode: '',
    morningNotificationTime: '09:00',
    upcomingNotificationMinutes: '10',
    missedNotificationMinutes: '60'
  });
  const [agencySettings, setAgencySettings] = React.useState({
    agencyName: 'Abhay Agency',
    agencyPhone: '+91 98250 12345',
    agencyPhone2: '',
    agencyEmail: 'info@abhayagency.com',
    agencyAddress: 'Shop No. 12, Ground Floor, Shreepal Complex, Ring Road',
    agencyCity: 'Surat',
    agencyState: 'Gujarat',
    agencyStateCode: '24',
    agencyPincode: '395002',
    agencyGst: '24AABCA1234F1Z5',
    agencyPan: 'AABCA1234F',
    bankName: 'State Bank of India',
    accountName: 'Abhay Agency',
    accountNumber: '38291048591',
    accountType: 'Current Account',
    branchName: 'SURAT',
    ifscCode: 'SBIN0001234',
    upiId: 'abhayagency@sbi',
    authorizedSignatoryLabel: 'For Abhay Agency',
    authorizedSignatoryName: 'Authorized Signatory',
    defaultTerms: '',
    defaultNotes: ''
  });
  const [loading, setLoading] = React.useState(true);
  const [errors, setErrors] = React.useState({});
  const [savingQr, setSavingQr] = React.useState(false);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { default: api } = await import('../../utils/api');
        const [res, invRes] = await Promise.allSettled([
          api.get('/settings'),
          api.get('/proforma-invoices/defaults')
        ]);

        if (res.status === 'fulfilled' && res.value?.data) {
          const d = res.value.data;
          setSettings({
            smtpHost: d.smtpHost || '',
            smtpPort: d.smtpPort || '',
            smtpUser: d.smtpUser || '',
            smtpPass: d.smtpPass || '',
            adminNotificationEmail: d.adminNotificationEmail || d.admin_notification_email || '',
            paymentQrCode: d.paymentQrCode || '',
            morningNotificationTime: d.morningNotificationTime || '09:00',
            upcomingNotificationMinutes: d.upcomingNotificationMinutes || '10',
            missedNotificationMinutes: d.missedNotificationMinutes || '60'
          });
        }

        if (invRes.status === 'fulfilled' && invRes.value?.data?.defaults) {
          const id = invRes.value.data.defaults;
          setAgencySettings({
            agencyName: id.agencyName || 'Abhay Agency',
            agencyPhone: id.agencyPhone || '+91 98250 12345',
            agencyPhone2: id.agencyPhone2 || '',
            agencyEmail: id.agencyEmail || 'info@abhayagency.com',
            agencyAddress: id.agencyAddress || 'Shop No. 12, Ground Floor, Shreepal Complex, Ring Road',
            agencyCity: id.agencyCity || 'Surat',
            agencyState: id.agencyState || 'Gujarat',
            agencyStateCode: id.agencyStateCode || '24',
            agencyPincode: id.agencyPincode || '395002',
            agencyGst: id.agencyGst || '24AABCA1234F1Z5',
            agencyPan: id.agencyPan || (id.agencyGst && id.agencyGst.length >= 12 ? id.agencyGst.substring(2, 12) : 'AABCA1234F'),
            bankName: id.bankName || 'State Bank of India',
            accountName: id.accountName || 'Abhay Agency',
            accountNumber: id.accountNumber || '38291048591',
            accountType: id.accountType || 'Current Account',
            branchName: id.branchName || 'SURAT',
            ifscCode: id.ifscCode || 'SBIN0001234',
            upiId: id.upiId || 'abhayagency@sbi',
            authorizedSignatoryLabel: id.authorizedSignatoryLabel || 'For Abhay Agency',
            authorizedSignatoryName: id.authorizedSignatoryName || 'Authorized Signatory',
            defaultTerms: id.defaultTerms || '',
            defaultNotes: id.defaultNotes || ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch settings', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const [savingSection, setSavingSection] = React.useState(null);

  const handleSaveAgencySettings = async (e) => {
    if (e) e.preventDefault();
    setSavingSection('agency');
    try {
      const { default: api } = await import('../../utils/api');
      await api.put('/proforma-invoices/defaults', agencySettings);
      toast.success('Abhay Agency profile & invoice settings saved successfully!');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save agency profile');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveSettings = async (e, successMsg = 'Settings saved successfully!', section = 'general') => {
    if (e) e.preventDefault();
    setSavingSection(section);
    try {
      const { default: api } = await import('../../utils/api');
      await api.post('/settings', settings);
      toast.success(successMsg);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        toast.error('Please fix the errors in the form');
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to save settings');
      }
    } finally {
      setSavingSection(null);
    }
  };

  const handleQrImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        return toast.error("Please upload a valid image file (PNG, JPG, WEBP)");
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings(prev => ({ ...prev, paymentQrCode: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveQrCode = async () => {
    try {
      setSavingQr(true);
      const { default: api } = await import('../../utils/api');
      await api.post('/settings', { paymentQrCode: settings.paymentQrCode });
      // Re-fetch to get updated file path if base64 was processed
      const res = await api.get('/settings');
      if (res.data && res.data.paymentQrCode) {
        setSettings(prev => ({ ...prev, paymentQrCode: res.data.paymentQrCode }));
      }
      toast.success('Payment QR Code updated successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save Payment QR Code');
    } finally {
      setSavingQr(false);
    }
  };

  const getQrImageUrl = () => {
    if (!settings.paymentQrCode || typeof settings.paymentQrCode !== 'string') return null;
    const val = settings.paymentQrCode.trim();
    if (!val) return null;
    if (val.startsWith('data:') || val.startsWith('http://') || val.startsWith('https://')) {
      return val;
    }
    const cleanPath = val.startsWith('/') ? val : `/${val}`;
    return `${BASE_URL}${cleanPath}`;
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Settings</h2>

      {/* Abhay Agency Profile & Proforma Invoice Settings Card */}
      <div className="card mb-6" style={{ marginBottom: '1.75rem', border: '1px solid var(--color-border)', borderRadius: '10px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          borderBottom: '1px solid var(--color-border)',
          paddingBottom: '1rem',
          marginBottom: '1.25rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-primary)'
            }}>
              <Building2 size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-lg" style={{ margin: 0, color: 'var(--color-text-main)' }}>
                Abhay Agency Profile & Proforma Invoice Settings
              </h3>
              <p className="text-xs text-muted" style={{ margin: '2px 0 0 0' }}>
                Manage seller profile, GSTIN, default bank accounts, terms & conditions for all proforma invoices
              </p>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={handleSaveAgencySettings}
            disabled={loading || savingSection === 'agency'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.55rem 1.25rem', fontWeight: 600 }}
          >
            {savingSection === 'agency' && <Loader2 size={16} className="spinner-icon" />}
            {savingSection === 'agency' ? 'Saving Agency Details...' : 'Save Agency Settings'}
          </button>
        </div>

        {/* 1. Agency Business Details (Seller Profile) */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem' }}>
            <Building2 size={16} /> Company Details (Seller Profile)
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div>
              <label className="text-xs font-semibold mb-1 block">Company / Agency Name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.agencyName}
                onChange={(e) => setAgencySettings({ ...agencySettings, agencyName: e.target.value })}
                placeholder="Abhay Agency"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Phone Number 1 <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.agencyPhone}
                onChange={(e) => setAgencySettings({ ...agencySettings, agencyPhone: e.target.value })}
                placeholder="+91 98250 12345"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Phone Number 2 (Optional)</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.agencyPhone2}
                onChange={(e) => setAgencySettings({ ...agencySettings, agencyPhone2: e.target.value })}
                placeholder="+91 98250 67890"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Email Address <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input
                type="email"
                className="input-field"
                value={agencySettings.agencyEmail}
                onChange={(e) => setAgencySettings({ ...agencySettings, agencyEmail: e.target.value })}
                placeholder="info@abhayagency.com"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">GSTIN (GST Number) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.agencyGst}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  const autoPan = val.length >= 12 ? val.substring(2, 12) : agencySettings.agencyPan;
                  const autoCode = val.length >= 2 && !isNaN(val.substring(0, 2)) ? val.substring(0, 2) : agencySettings.agencyStateCode;
                  setAgencySettings({
                    ...agencySettings,
                    agencyGst: val,
                    agencyPan: autoPan,
                    agencyStateCode: autoCode
                  });
                }}
                placeholder="24AABCA1234F1Z5"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">PAN NO <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.agencyPan}
                onChange={(e) => setAgencySettings({ ...agencySettings, agencyPan: e.target.value.toUpperCase() })}
                placeholder="AABCA1234F"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">State Name / Code</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '0.5rem' }}>
                <input
                  type="text"
                  className="input-field"
                  value={agencySettings.agencyState}
                  onChange={(e) => setAgencySettings({ ...agencySettings, agencyState: e.target.value })}
                  placeholder="Gujarat"
                  title="State Name"
                />
                <input
                  type="text"
                  className="input-field"
                  value={agencySettings.agencyStateCode}
                  onChange={(e) => setAgencySettings({ ...agencySettings, agencyStateCode: e.target.value })}
                  placeholder="24"
                  title="State Code"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">City & Pincode</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.5rem' }}>
                <input
                  type="text"
                  className="input-field"
                  value={agencySettings.agencyCity}
                  onChange={(e) => setAgencySettings({ ...agencySettings, agencyCity: e.target.value })}
                  placeholder="Surat"
                />
                <input
                  type="text"
                  className="input-field"
                  value={agencySettings.agencyPincode}
                  onChange={(e) => setAgencySettings({ ...agencySettings, agencyPincode: e.target.value })}
                  placeholder="395002"
                />
              </div>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="text-xs font-semibold mb-1 block">Address / Street</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.agencyAddress}
                onChange={(e) => setAgencySettings({ ...agencySettings, agencyAddress: e.target.value })}
                placeholder="Shop No. 12, Ground Floor, Shreepal Complex, Ring Road"
              />
            </div>
          </div>
        </div>

        {/* 2. Bank Details */}
        <div style={{ marginBottom: '1.5rem', borderTop: '1px solid var(--color-border)', paddingTop: '1.25rem' }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem' }}>
            <CreditCard size={16} /> Default Bank Details (For Invoice Printing)
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div>
              <label className="text-xs font-semibold mb-1 block">Company Name</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.accountName}
                onChange={(e) => setAgencySettings({ ...agencySettings, accountName: e.target.value })}
                placeholder="Abhay Agency"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Bank Name</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.bankName}
                onChange={(e) => setAgencySettings({ ...agencySettings, bankName: e.target.value })}
                placeholder="State Bank of India"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">A/C No</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.accountNumber}
                onChange={(e) => setAgencySettings({ ...agencySettings, accountNumber: e.target.value })}
                placeholder="38291048590"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">A/C Type</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.accountType}
                onChange={(e) => setAgencySettings({ ...agencySettings, accountType: e.target.value })}
                placeholder="Current Account"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Branch</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.branchName}
                onChange={(e) => setAgencySettings({ ...agencySettings, branchName: e.target.value })}
                placeholder="SURAT"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">IFSC Code</label>
              <input
                type="text"
                className="input-field"
                value={agencySettings.ifscCode}
                onChange={(e) => setAgencySettings({ ...agencySettings, ifscCode: e.target.value.toUpperCase() })}
                placeholder="SBIN0001234"
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveAgencySettings}
            disabled={loading || savingSection === 'agency'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.65rem 1.5rem', fontWeight: 600 }}
          >
            {savingSection === 'agency' && <Loader2 size={16} className="spinner-icon" />}
            {savingSection === 'agency' ? 'Saving Agency Settings...' : 'Save Agency Settings'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>

        {/* Left Column: Payment QR Code & Notification Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Notification Settings */}
          <div className="card">
            <h3 className="font-semibold mb-4">Notification Settings</h3>
            <form className="flex-col gap-4" noValidate>
              <div>
                <label className="text-sm font-semibold mb-1 block">Admin Email for Lead Generation Notifications <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="email" required className="input-field" value={settings.adminNotificationEmail || ''} onChange={(e) => setSettings({ ...settings, adminNotificationEmail: e.target.value })} placeholder="admin@agency.com" />
              </div>
              <button type="button" className="btn btn-primary mt-2" onClick={(e) => {
                const newErrors = {};
                if (!settings.adminNotificationEmail) newErrors.adminNotificationEmail = 'Admin Email is required';
                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return toast.error('Please fix the errors in the form');
                }
                setErrors({});
                handleSaveSettings(e, 'Notification Settings saved!', 'notification');

              }} disabled={loading || savingSection === 'notification'}>
                {savingSection === 'notification' && <Loader2 size={16} className="spinner-icon" />}
                {savingSection === 'notification' ? 'Saving...' : 'Save Settings'}
              </button>
            </form>
          </div>

          {/* Cron Timing Settings */}
          <div className="card">
            <h3 className="font-semibold mb-4">Cron Timing Settings</h3>
            <form className="flex-col gap-4" noValidate>
              <div>
                <label className="text-sm font-semibold mb-1 block">Morning Notification Time <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="time" required className="input-field" value={settings.morningNotificationTime || ''} onChange={(e) => setSettings({ ...settings, morningNotificationTime: e.target.value })} />
                <p className="text-xs text-muted mt-1">Time to send the "Good Morning" notification (IST)</p>
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Upcoming Notification Before (Minutes) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="number" required className="input-field" value={settings.upcomingNotificationMinutes || ''} onChange={(e) => setSettings({ ...settings, upcomingNotificationMinutes: e.target.value })} placeholder="10" />
                <p className="text-xs text-muted mt-1">Send a reminder this many minutes before a scheduled follow-up or delivery</p>
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Missed Escalation After (Minutes) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="number" required className="input-field" value={settings.missedNotificationMinutes || ''} onChange={(e) => setSettings({ ...settings, missedNotificationMinutes: e.target.value })} placeholder="60" />
                <p className="text-xs text-muted mt-1">Escalate to admin and user this many minutes after a missed follow-up or delivery</p>
              </div>
              <button type="button" className="btn btn-primary mt-2" onClick={(e) => {
                const newErrors = {};
                if (!settings.morningNotificationTime) newErrors.morningNotificationTime = 'Required';
                if (!settings.upcomingNotificationMinutes) newErrors.upcomingNotificationMinutes = 'Required';
                if (!settings.missedNotificationMinutes) newErrors.missedNotificationMinutes = 'Required';

                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return toast.error('Please fix the errors in the form');
                }
                setErrors({});
                handleSaveSettings(e, 'Cron Timings saved!', 'cron');

              }} disabled={loading || savingSection === 'cron'}>
                {savingSection === 'cron' && <Loader2 size={16} className="spinner-icon" />}
                {savingSection === 'cron' ? 'Saving...' : 'Save Timings'}
              </button>
            </form>
          </div>

        </div>

        {/* Right Column: SMTP Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Payment QR Code Settings */}
          <div className="card">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <QrCode size={20} color="var(--color-primary)" /> Payment QR Code
            </h3>
            <p className="text-sm text-muted mb-4">
              Upload the company payment QR code (UPI / GPay / PhonePe / Bank QR) to display in Delivery Details for customers and delivery boys.
            </p>

            <div className="flex flex-col gap-4">
              {settings.paymentQrCode && getQrImageUrl() ? (
                <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: '#f8fafc', textAlign: 'center' }}>
                  <img
                    src={getQrImageUrl()}
                    alt="Payment QR Code"
                    onError={(e) => {
                      console.error("Failed to load QR image:", getQrImageUrl());
                    }}
                    style={{ maxWidth: '220px', maxHeight: '220px', objectFit: 'contain', margin: '0 auto', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', padding: '8px', display: 'block' }}
                  />
                  <div className="flex gap-2 justify-center mt-3">
                    <label className="btn text-sm" style={{ border: '1px solid var(--color-border)', backgroundColor: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Upload size={14} /> Replace QR Image
                      <input type="file" accept="image/*" onChange={handleQrImageChange} style={{ display: 'none' }} />
                    </label>
                    <button
                      type="button"
                      className="btn text-danger text-sm"
                      onClick={async () => {
                        setSettings(prev => ({ ...prev, paymentQrCode: '' }));
                        try {
                          const { default: api } = await import('../../utils/api');
                          await api.post('/settings', { paymentQrCode: '' });
                          toast.success("Payment QR Code removed");
                        } catch (err) {
                          toast.error("Failed to remove QR code");
                        }
                      }}
                      style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ border: '2px dashed var(--color-border)', borderRadius: '8px', padding: '2rem', textAlign: 'center', backgroundColor: '#fafafa' }}>
                  <QrCode size={36} color="#94a3b8" style={{ margin: '0 auto 0.5rem auto' }} />
                  <p className="text-sm font-semibold text-main mb-1">No Payment QR Code Uploaded</p>
                  <p className="text-xs text-muted mb-3">Upload your UPI / Payment QR code image</p>
                  <label className="btn btn-primary text-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Upload size={16} /> Choose Image
                    <input type="file" accept="image/*" onChange={handleQrImageChange} style={{ display: 'none' }} />
                  </label>
                </div>
              )}

              {settings.paymentQrCode && (
                <button
                  type="button"
                  className="btn btn-primary mt-2"
                  onClick={handleSaveQrCode}
                  disabled={savingQr || loading}
                >
                  {savingQr ? 'Saving...' : 'Save Payment QR Code'}
                </button>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold mb-4">SMTP Settings</h3>
            <form className="flex-col gap-4" noValidate>
              <div>
                <label className="text-sm font-semibold mb-1 block">SMTP Host <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="text" required className="input-field" value={settings.smtpHost || ''} onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })} placeholder="smtp.gmail.com" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">SMTP Port <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="number" required className="input-field" value={settings.smtpPort || ''} onChange={(e) => setSettings({ ...settings, smtpPort: e.target.value })} placeholder="587" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Username / Email <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="email" required className="input-field" value={settings.smtpUser || ''} onChange={(e) => setSettings({ ...settings, smtpUser: e.target.value })} placeholder="hello@agency.com" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Password <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input type="password" required className="input-field" value={settings.smtpPass || ''} onChange={(e) => setSettings({ ...settings, smtpPass: e.target.value })} placeholder="*********" />
              </div>
              <button type="button" className="btn btn-primary mt-2" onClick={(e) => {
                const newErrors = {};
                if (!settings.smtpHost) newErrors.smtpHost = 'SMTP Host is required';
                if (!settings.smtpPort) newErrors.smtpPort = 'SMTP Port is required';
                if (!settings.smtpUser) newErrors.smtpUser = 'Username / Email is required';
                if (!settings.smtpPass) newErrors.smtpPass = 'Password is required';
                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return toast.error('Please fix the errors in the form');
                }
                setErrors({});
                handleSaveSettings(e, 'SMTP Settings saved!', 'smtp');

              }} disabled={loading || savingSection === 'smtp'}>
                {savingSection === 'smtp' && <Loader2 size={16} className="spinner-icon" />}
                {savingSection === 'smtp' ? 'Saving...' : 'Save SMTP'}
              </button>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Settings;
